import { Location } from '@angular/common';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { MonoTypeOperatorFunction, Observable, throwError, timer } from 'rxjs';
import { catchError, retry } from 'rxjs/operators';
import { X_SKIP_ALERT } from '../constants/http-headers';
import { ThErrorResponse } from '../models/th-error-response';
import { AuthService } from '../services/auth.service';
import { EnvironmentService } from '../services/environment.service';
import { GlobalModalService } from '../services/global-modal.service';
import { isObject, isString } from '../utils/type-util';

const SITE_DOWN_PATH = '/site-down';
const REDIS_EXCEPTION = 'org.springframework.data.redis.RedisConnectionFailureException';
const EXCLUDED_STATUS_CODES = new Set([0, 302, 401, 403, 404, 504]);
export const SITE_DOWN_STATUS_CODES = new Set([503, 504]);

/**
 * Provides basic error intercepting for all HTTP requests.
 * Also includes a retry mechanism for requests that meet a certain criteria.
 */
@Injectable({
  providedIn: 'root'
})
export class ErrorHandlingInterceptor implements HttpInterceptor {
  constructor(
    private authService: AuthService,
    private environmentService: EnvironmentService,
    private globalModalService: GlobalModalService,
    private modalService: NgbModal,
    private router: Router,
    private location: Location
  ) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      this.genericRetryWithDelay(),
      catchError((errorResponse: HttpErrorResponse) => {
        return this.handleError(errorResponse, req);
      })
    );
  }

  private handleError(errorResponse: HttpErrorResponse, req: HttpRequest<unknown>) {
    if (errorResponse?.status === 401) {
      this.modalService.dismissAll();
      const serverError = errorResponse.error as ThErrorResponse;
      const redirect = serverError?.redirect || '/logout';
      this.router.navigate([redirect]);
      return throwError(() => errorResponse);
    }

    if (this.environmentService.enableSiteDownRedirect && SITE_DOWN_STATUS_CODES.has(errorResponse?.status)) {
      const currentPath = this.location.path();

      if (currentPath !== SITE_DOWN_PATH) {
        this.authService.siteDownRedirect = this.location.path();
      }

      this.router.navigate([SITE_DOWN_PATH]);

      return throwError(() => errorResponse);
    }

    const showAlert = () => {
      const skipAlert = req.headers.get(X_SKIP_ALERT) === '1';

      // Allow API calls to opt out of global alert messaging
      if (!skipAlert) {
        this.globalModalService.alert({
          headerText: 'Error',
          bodyText: this.getErrorText(errorResponse)
        });
      }
    };

    const serverErrorJson = <ThErrorResponse>errorResponse.error;

    if (serverErrorJson?.redirect) {
      this.modalService.dismissAll();

      this.router.navigate([serverErrorJson.redirect]).finally(() => {
        // Showing the alert AFTER the redirect finishes to prevent weirdness
        // with table width recalculation
        showAlert();
      });
    } else {
      showAlert();
    }

    // Just rethrow the error so any subscribers can react to it
    return throwError(() => errorResponse);
  }

  private getErrorText(errorResponse: HttpErrorResponse): string {
    if (errorResponse && isObject(errorResponse.error)) {
      return (
        (errorResponse.error as ThErrorResponse).message || 'An unexpected error occurred. Please try again later.'
      );
    } else if (errorResponse instanceof HttpErrorResponse) {
      return errorResponse.message;
    } else {
      return String(errorResponse);
    }
  }

  private genericRetryWithDelay({
    maxRetryAttempts = 3,
    scalingDuration = 100,
    excludedStatusCodes = EXCLUDED_STATUS_CODES
  }: {
    maxRetryAttempts?: number;
    scalingDuration?: number;
    excludedStatusCodes?: Set<number>;
  } = {}): MonoTypeOperatorFunction<HttpEvent<unknown>> {
    return retry({
      count: maxRetryAttempts,
      delay: (error: HttpErrorResponse, retryAttempt: number) => {
        // if maximum number of retries have been met
        // or response is a status code we don't wish to retry
        // or it isn't a Redis exception, throw error
        if (
          retryAttempt >= maxRetryAttempts ||
          excludedStatusCodes.has(error.status) ||
          !this.isRedisException(error)
        ) {
          return throwError(() => error);
        }

        const nextRetry = retryAttempt * scalingDuration;

        console.log(`Attempt ${retryAttempt}: retrying in ${nextRetry}ms for URL ${error.url}`);

        // retry after 1s, 2s, etc...
        return timer(nextRetry);
      }
    });
  }

  private isRedisException(errorResponse: HttpErrorResponse): boolean {
    if (isObject(errorResponse?.error)) {
      return (errorResponse.error as ThErrorResponse).type === REDIS_EXCEPTION;
    } else if (isString(errorResponse?.error)) {
      return errorResponse.error.indexOf(REDIS_EXCEPTION) >= 0;
    }

    return false;
  }
}
